import React, { useEffect, useRef, useState } from 'react';
import { IMAGES } from '../../assets/marketing/manifest';
import type { StripItem } from '../content/home';
import { focal } from '../content/images';
import useReducedMotion from '../hooks/useReducedMotion';
import Picture from '../primitives/Picture';

interface ImageStripProps {
  items: StripItem[];
  /** Accessible name for the tab list, e.g. "Why Melio". */
  label: string;
}

const MOBILE = '(max-width: 767px)';

/**
 * The four-up strip under "WHY MELIO?" (guidelines p.53): one image in focus,
 * the rest at 15% opacity, with the focused item's caption and paragraph
 * beneath it. Built as a tab list so the images are honestly interactive:
 * hover, focus, click and the arrow keys move the focus; while the strip is
 * on screen and the pointer is elsewhere, scrolling through it walks the
 * focus along (skipped under reduced motion). Below 768px it becomes a
 * scroll-snap row and the most visible slide is the focused one.
 */
export default function ImageStrip({ items, label }: ImageStripProps) {
  const [focus, setFocus] = useState(0);
  const stripRef = useRef<HTMLDivElement | null>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const pointerWithin = useRef(false);
  const reduced = useReducedMotion();
  const count = items.length;

  // Desktop: scroll progress through the viewport picks the focused image.
  useEffect(() => {
    if (reduced || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    if (window.matchMedia(MOBILE).matches) return undefined;
    const node = stripRef.current;
    if (!node) return undefined;
    let raf = 0;
    const onScroll = () => {
      if (pointerWithin.current || raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const rect = node.getBoundingClientRect();
        const vh = window.innerHeight;
        const progress = (vh - rect.top) / (vh + rect.height); // 0 entering → 1 leaving
        if (progress > 0.15 && progress < 0.85) {
          setFocus(Math.min(count - 1, Math.max(0, Math.floor(((progress - 0.15) / 0.7) * count))));
        }
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [count, reduced]);

  // Mobile: the slide that is mostly on screen is the focused one.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return undefined;
    if (typeof window.matchMedia !== 'function' || !window.matchMedia(MOBILE).matches) return undefined;
    const node = stripRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const index = tabRefs.current.indexOf(entry.target as HTMLButtonElement);
          if (index >= 0) setFocus(index);
        });
      },
      { root: node, threshold: 0.6 },
    );
    tabRefs.current.forEach((tab) => tab && observer.observe(tab));
    return () => observer.disconnect();
  }, [count]);

  const move = (next: number) => {
    const index = (next + count) % count;
    setFocus(index);
    tabRefs.current[index]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    const map: Record<string, number> = {
      ArrowRight: focus + 1,
      ArrowLeft: focus - 1,
      Home: 0,
      End: count - 1,
    };
    const next = map[event.key];
    if (next === undefined) return;
    event.preventDefault();
    move(next);
  };

  return (
    <div>
      <div
        ref={stripRef}
        className="mk-strip"
        role="tablist"
        aria-label={label}
        onKeyDown={onKeyDown}
        onMouseEnter={() => {
          pointerWithin.current = true;
        }}
        onMouseLeave={() => {
          pointerWithin.current = false;
        }}
      >
        {items.map((item, index) => {
          const selected = index === focus;
          return (
            <button
              key={item.id}
              ref={(el) => {
                tabRefs.current[index] = el;
              }}
              type="button"
              role="tab"
              id={`${item.id}-tab`}
              className="mk-strip__tab"
              aria-selected={selected}
              aria-controls={`${item.id}-panel`}
              aria-label={item.caption}
              tabIndex={selected ? 0 : -1}
              onMouseEnter={() => setFocus(index)}
              onFocus={() => setFocus(index)}
              onClick={() => setFocus(index)}
            >
              <Picture
                image={IMAGES[item.image]}
                alt=""
                sizes="(min-width: 768px) 25vw, 80vw"
                focal={focal(item.image)}
                style={{ aspectRatio: '4 / 3' }}
              />
            </button>
          );
        })}
      </div>
      <div className="mk-strip__panels">
        {items.map((item, index) => (
          <div
            key={item.id}
            id={`${item.id}-panel`}
            role="tabpanel"
            aria-labelledby={`${item.id}-tab`}
            hidden={index !== focus}
            className="mk-strip__panel"
            style={{ gridColumn: `${index + 1}` }}
          >
            <p className="t-details">{item.caption}</p>
            <p className="t-body">{item.body}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
